import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  IconButton,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Switch,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Chip
} from "@material-ui/core";
import ClearOutlinedIcon from "@material-ui/icons/ClearOutlined";
import SaveAltOutlinedIcon from "@material-ui/icons/SaveAltOutlined";
import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";

import { setUploadStopsOpen } from "../../store/actions/account";
import {
  convertTimeToHoursAndMins,
  numberWithCommas,
  remCalc,
  exportToXlsx,
  getTimeStr
} from "../../utils/helpers";
import { setInfoDialog } from "../../store/actions/common";
import { fields } from "../data/import/fields";
import ExampleTable from "../import/ExampleTable";
import { servicesExample } from "../data/import/examples";
import API from "../../utils/axios";
import mixpanelWrapper from "../../utils/mixpanel";
import history from "../../utils/history";
import { setProject } from "../../store/actions/project";

import DragAndDrop from "./DragAndDrop";
import { cellValidator } from "./utils";

import "ag-grid-community/dist/styles/ag-grid.css";
import "./styles.css";
import DataTable from "../import/DataTable";
import TitleWithEducationalMaterials from "../common/TitleWithEducationalMaterials";

const useStyles = makeStyles(theme => ({
  dialogRoot: {
    zIndex: [1304, "!important"]
  },
  dialogPaper: {
    minHeight: "calc(100% - 64px)",
    height: "calc(100% - 64px)",
    position: "relative"
  },
  dialogTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightMedium
  },
  importFileSectionWrapper: {
    margin: theme.spacing(4, 0),
    maxWidth: "100%",
    overflow: "auto"
  },
  importFileSectionTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(2)
  },
  chip: {
    height: remCalc(28),
    color: "#808080",
    fontSize: remCalc(14),
    backgroundColor: "#EEE",
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  chipDark: {
    color: "#fff",
    backgroundColor: "#808080"
  },
  exampleTableWrapper: {
    overflow: "auto",
    backgroundColor: "#F7F7F7",
    borderRadius: 4,
    border: "1px dashed #eee",
    maxWidth: "100%"
  },
  mappingLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium
  },
  mappingDescription: {
    fontSize: theme.typography.pxToRem(16),
    marginBottom: theme.spacing(2)
  },
  columnSelectPopoverRoot: {
    zIndex: [1305, "!important"]
  },
  tableInfo: {
    fontSize: theme.typography.pxToRem(14),
    marginTop: theme.spacing(2),
    color: "#808080"
  },
  otherDialogRoots: {
    zIndex: [1306, "!important"]
  },
  loadingWrapper: {
    backgroundColor: "rgba(255,255,255, 0.5) !important",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1306,
    "& .MuiCircularProgress-root": {
      position: "absolute"
    }
  },
  actionButton: {
    backgroundColor: [theme.palette.primary.main, "!important"],
    height: 36,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: [theme.palette.primary.dark, "!important"]
    }
  },
  simpleVersionButton: {
    color: "#808080",
    textTransform: "none",
    fontSize: theme.typography.body2.fontSize,
    padding: theme.spacing(0.75, 1)
  },
  cancelButton: {
    backgroundColor: ["transparent", "!important"],
    height: 36,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: ["transparent", "!important"]
    }
  },
  generalDialogTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.typography.fontWeightMedium
  },
  mappingTableWrapper: {
    width: 500,
    border: "1px solid #EEEEEE"
  },
  mappingTableHeader: {
    background: "#F4F4F4"
  },
  showOnlyRowsWithProblems: {
    marginBottom: theme.spacing(2),
    paddingLeft: 4,
    position: "relative",
    display: "inline-block",
    zIndex: 3
  },
  showOnlyRowsWithProblemsPulse: {
    display: "block",
    position: "absolute",
    width: "100%",
    height: "100%",
    borderRadius: theme.spacing(0.5),
    top: "0",
    left: "0",
    opacity: "0",
    backgroundColor: "rgba(19,41,254, 0.6)",
    transition: "opacity .9s, transform .9s",
    animation: "none",
    animationIterationCount: "3 !important"
  }
}));

const stages = {
  CHOOSE_FILE: "CHOOSE_FILE",
  MAPPING_COLUMNS: "MAPPING_COLUMNS",
  INTERACTIVE_TABLE: "INTERACTIVE_TABLE",
  VALIDATION_RESULT: "VALIDATION_RESULT"
};

const columnIds = [
  "type",
  "name",
  "street",
  "city",
  "postal_code",
  "country_code",
  "lat",
  "long",
  "duration",
  "from",
  "to",
  "email",
  "phone",
  "priority",
  "pod_signature",
  "pod_age",
  "pod_photo",
  "delivery_note",
  "vehicle_tags",
  "driver_tags",
  "unit_count",
  "unit_weight",
  "unit_volume",
  "unit_id",
  "unit_note",
  "tracking_id"
];

const requiredColumnIds = ["type", "street", "postal_code", "country_code"];

const columnTitles = {
  type: "Type",
  name: "Name",
  street: "Street",
  city: "City",
  postal_code: "Postal code",
  country_code: "Country code",
  lat: "Lat",
  long: "Long",
  duration: "Duration",
  from: "From",
  to: "To",
  email: "Email",
  phone: "Phone",
  priority: "Priority",
  pod_signature: "POD signature",
  pod_age: "POD age",
  pod_photo: "POD photo",
  delivery_note: "Delivery note",
  vehicle_tags: "Vehicle tags",
  driver_tags: "Driver tags",
  unit_count: "Unit count",
  unit_weight: "Unit weight",
  unit_volume: "Unit volume",
  unit_id: "Unit id",
  unit_note: "Unit note",
  tracking_id: "Tracking id"
};

const UploadStopsDialog = props => {
  const {
    uploadStopsOpen,
    setUploadStopsOpen,
    plan,
    setInfoDialog,
    project,
    setProject
  } = props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [stage, setStage] = useState(stages.CHOOSE_FILE);
  const [fileContent, setFileContent] = useState([]);
  const [fileColumnIds, setFileColumnIds] = useState([]);
  const [columnsMapping, setColumnsMapping] = useState({});
  const [mainStops, setMainStops] = useState([]);
  const [invalidStops, setInvalidStops] = useState([]);
  const [downloadRowsDialogOpen, setDownloadRowsDialogOpen] = useState(false);
  const [downloadOnlyInvalidFields, setDownloadOnlyInvalidFields] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState({
    invalid: [],
    valid: []
  });
  const [showOnlyInvalidStops, setShowOnlyInvalidStops] = useState(false);

  // Refs
  const tableRef = useRef(null);
  const showOnlyRowsWithProblemPulseRef = useRef(null);

  // Callbacks
  const resetState = () => {
    setTimeout(() => {
      setStage(stages.CHOOSE_FILE);
      setMainStops([]);
      setFileContent([]);
      setFileColumnIds([]);
      setColumnsMapping([]);
      setInvalidStops([]);
      setDownloadRowsDialogOpen(false);
      setDownloadOnlyInvalidFields(false);
      setValidationResult({ invalid: [], valid: [] });
    }, 150);
  };

  const validateStop = useCallback(row => {
    for (const columnId in row) {
      if (!cellValidator(columnId, row[columnId], row)) {
        return false;
      }
    }
    return true;
  }, []);

  const validateStops = useCallback(
    stops => {
      const tempInvalidStops = [];
      stops.forEach(stop => {
        if (!validateStop(stop)) {
          tempInvalidStops.push(stop.row);
        }
      });
      setInvalidStops(tempInvalidStops);
    },
    [validateStop]
  );

  const loadFileContent = useCallback(
    ({ body, blank: isBlank, data }) => {
      if (isBlank) {
        enqueueSnackbar(
          "The imported file is empty. Please check and import correct file again."
        );
        return;
      }
      const finalFileContent = body || data;

      const tempMappingFields = Object.fromEntries(
        columnIds.map(columnId => [columnId, ""])
      );

      const tempFileColumnIds = [];
      if (finalFileContent[0]) {
        Object.keys(finalFileContent[0]).forEach(columnId => {
          tempFileColumnIds.push(columnId);
          if (columnIds.includes(columnId)) {
            tempMappingFields[columnId] = columnId;
          }
        });
      }
      setFileColumnIds(tempFileColumnIds);
      setColumnsMapping(tempMappingFields);
      setFileContent(finalFileContent);
      setStage(stages.MAPPING_COLUMNS);
    },
    [enqueueSnackbar]
  );

  const validateMappingAndGoNext = useCallback(() => {
    let missingRequiredFields = [];
    requiredColumnIds.forEach(requiredColumnId => {
      if (columnsMapping[requiredColumnId] === "") {
        if (
          ["street", "postal_code", "country_code"].includes(
            requiredColumnId
          ) &&
          columnsMapping.lat !== "" &&
          columnsMapping.long !== ""
        ) {
          return;
        }
        missingRequiredFields.push(requiredColumnId);
      }
    });

    if (missingRequiredFields.length > 0) {
      enqueueSnackbar(
        `These columns are required and missing a mapped column id. Please map these columns.${missingRequiredFields
          .map(columnId => columnTitles[columnId])
          .join(", ")}`
      );
      return;
    }

    let stops = fileContent.filter(
      obj => Object.keys(obj).filter(el => obj[el]).length
    );

    // Check if it's not bigger than the maximum caps
    if (!plan.cap_rows_per_upload || stops.length > plan.cap_rows_per_upload) {
      setInfoDialog({
        visible: true,
        title: "Operation not possible",
        body: `The maximum number of rows per upload is ${numberWithCommas(
          plan.cap_rows_per_upload || 0
        )}.`
      });
      return;
    }

    stops = stops.map((stop, index) => {
      const finalStopData = {
        row: String(index + 2)
      };
      Object.keys(columnsMapping).forEach(columnId => {
        const value = stop[columnsMapping[columnId]];
        switch (columnId) {
          case "type":
            finalStopData[columnId] =
              typeof value === "string" ? value.trim().toLowerCase() : value;
            break;
          case "long":
          case "lat":
            finalStopData[columnId] = isNaN(value) ? null : value;
            break;
          case "from":
          case "to":
            finalStopData[columnId] = value
              ? isNaN(Date.parse(value))
                ? convertTimeToHoursAndMins(value)
                : format(new Date(value), "HH:mm")
              : value;
            break;
          default:
            finalStopData[columnId] =
              typeof value === "string" ? value.trim() : value;
            break;
        }
      });
      return finalStopData;
    });

    setMainStops(stops);
    validateStops(stops);

    setStage(stages.INTERACTIVE_TABLE);
  }, [
    columnsMapping,
    fileContent,
    plan.cap_rows_per_upload,
    setInfoDialog,
    validateStops
  ]);

  const toXlsxExportData = useCallback(stopsData => {
    return [
      columnIds,
      ...stopsData.map(stop => columnIds.map(columnId => stop[columnId]))
    ];
  }, []);

  const submitValidateStops = useCallback(() => {
    if (invalidStops.length > 0) {
      enqueueSnackbar(
        `File has ${invalidStops.length} invalid ${
          invalidStops.length === 1 ? "row" : "rows"
        }. Please resolve the errors before continuing.`
      );
      if (showOnlyRowsWithProblemPulseRef.current) {
        showOnlyRowsWithProblemPulseRef.current.style.animation = "none";
        // Trigger reflow
        const offsetHeight =
          showOnlyRowsWithProblemPulseRef.current.offsetHeight;
        showOnlyRowsWithProblemPulseRef.current.style.animation =
          "pulse-animation 1s cubic-bezier(0.24, 0, 0.38, 1)";
      }
      return;
    }
    setLoading(true);
    API.post(`/v1/projects/${project.id}/services/validation`, {
      stops: mainStops.map(
        ({
          city,
          country_code,
          delivery_note,
          driver_tags,
          duration,
          email,
          from,
          lat,
          long,
          name,
          notes,
          phone,
          pod_age,
          pod_photo,
          pod_signature,
          postal_code,
          priority,
          row,
          street,
          to,
          type: theType,
          vehicle_tags,
          unit_count,
          unit_id,
          unit_note,
          unit_volume,
          unit_weight,
          tracking_id
        }) => ({
          city,
          country_code,
          duration,
          email,
          from,
          lat,
          long,
          name,
          notes: delivery_note,
          phone,
          pod_age,
          pod_photo,
          pod_signature,
          postal_code,
          priority,
          row,
          street,
          to,
          unit_count,
          unit_id,
          unit_note,
          volume: unit_volume,
          weight: unit_weight,
          type: theType,
          load: unit_count,
          driver_tags: driver_tags ? driver_tags.split("; ") : null,
          vehicle_tags: vehicle_tags ? vehicle_tags.split("; ") : null,
          tracking_id
        })
      )
    })
      .then(res => {
        setStage(stages.VALIDATION_RESULT);
        setValidationResult(res.data);
      })
      .catch(error => {
        console.dir(error);
        if (error && error.data && error.data.status === 400) {
          setInfoDialog({
            visible: true,
            title: "Please upgrade your plan",
            body: error.data.message
          });
        } else {
          enqueueSnackbar("Something went wrong while importing stops.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    enqueueSnackbar,
    invalidStops.length,
    mainStops,
    project.id,
    setInfoDialog,
    showOnlyInvalidStops
  ]);

  const addStops = useCallback(() => {
    setLoading(true);
    API.post(`/v1/projects/${project.id}/services/add`, validationResult)
      .then(res => {
        mixpanelWrapper.track("Stop Add", {
          Uploaded: true
        });
        setProject(res.data);
      })
      .catch(err => {
        console.dir(err);
        enqueueSnackbar("Something went wrong while importing stops.");
      })
      .finally(() => {
        setLoading(false);
        setUploadStopsOpen(false);
        resetState();
        history.replace(`/${project.id}`);
      });
  }, [
    enqueueSnackbar,
    project.id,
    setProject,
    setUploadStopsOpen,
    validationResult
  ]);

  const downloadValidationResultRows = useCallback(
    onlyInvalids => {
      const data = onlyInvalids
        ? validationResult.invalid.map(invalid => invalid[0])
        : [
            ...validationResult.valid,
            ...validationResult.invalid.map(invalid => invalid[0])
          ];
      exportToXlsx(onlyInvalids ? "invalid_stops.xlsx" : "all_stops.xlsx", [
        columnIds,
        ...data.map(rowData => {
          const row = rowData;
          return [
            row.type,
            row.information ? row.information.name : null,
            row.information ? row.information.street : null,
            row.information ? row.information.city : null,
            row.information ? row.information.zipcode : null,
            row.information ? row.information.country : null,
            row.address ? row.address.lat : null,
            row.address ? row.address.lng : null,
            row.duration,
            row.time_windows ? getTimeStr(row.time_windows[0].earliest) : null,
            row.time_windows ? getTimeStr(row.time_windows[0].latest) : null,
            row.information ? row.information.email : null,
            row.information && row.information.recipient_phone
              ? row.information.recipient_phone[0] === "+"
                ? row.information.recipient_phone
                : `+${row.information.recipient_phone}`
              : null,
            row.priority === 1 ? "Yes" : null,
            row.requirements && row.requirements.signature ? "Yes" : null,
            row.requirements && row.requirements.age_verification
              ? "Yes"
              : null,
            row.requirements && row.requirements.picture ? "Yes" : null,
            row.information ? row.information.notes : null,
            row.tags
              ?.filter(tag => tag.type === "VEHICLE")
              .map(el => el.name)
              .join("; "),
            row.tags
              ?.filter(tag => tag.type === "DRIVER")
              .map(el => el.name)
              .join("; "),
            row.quantity,
            row.weight,
            row.volume,
            row.units && row.units[0] ? row.units[0].name : null,
            row.units && row.units[0] ? row.units[0].note : null,
            row.tracking.id,
          ];
        })
      ]);
    },
    [validationResult.invalid]
  );

  // Memos
  const columnDefs = useMemo(
    () => [
      {
        valueGetter: ({ data }) => Number(data.row) - 1,
        width: 70,
        editable: false
      },
      {
        headerName: "Type",
        field: "type",
        cellEditor: "agPopupSelectCellEditor",
        cellEditorParams: {
          values: ["delivery", "pickup"]
        }
      },
      { headerName: "Name", field: "name", width: 200 },
      { headerName: "Street", field: "street", width: 200 },
      { headerName: "City", field: "city" },
      { headerName: "Postal code", field: "postal_code", width: 140 },
      { headerName: "Country code", field: "country_code", width: 140 },
      { headerName: "Lat", field: "lat" },
      { headerName: "Long", field: "long" },
      { headerName: "Duration", field: "duration" },
      { headerName: "From", field: "from" },
      { headerName: "To", field: "to" },
      { headerName: "Email", field: "email", width: 200 },
      { headerName: "Phone", field: "phone", width: 150 },
      { headerName: "Priority", field: "priority" },
      { headerName: "POD signature", field: "pod_signature", width: 150 },
      { headerName: "POD age", field: "pod_age" },
      { headerName: "POD Photo", field: "pod_photo" },
      { headerName: "Delivery note", field: "delivery_note", width: 200 },
      { headerName: "Vehicle tags", field: "vehicle_tags", width: 140 },
      { headerName: "Driver tags", field: "driver_tags", width: 140 },
      { headerName: "Unit count", field: "unit_count", width: 140 },
      { headerName: "Unit weight", field: "unit_weight", width: 140 },
      { headerName: "Unit volume", field: "unit_volume", width: 140 },
      { headerName: "Unit id", field: "unit_id" },
      { headerName: "Unit note", field: "unit_note", width: 200 },
      { headerName: "Tracking Id", field: "tracking_id", width: 200 }
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      editable: true,
      singleClickEdit: true,
      width: 104,
      cellClass: ({ colDef: { field }, data, value }) => {
        let cellClass = "";
        if (!cellValidator(field, value, data)) {
          cellClass = "invalid-cell";
        }
        return cellClass;
      },
      onCellValueChanged: ({ data, node }) => {
        setMainStops(currentMainStops => {
          currentMainStops[
            currentMainStops.findIndex(stop => stop.row === data.row)
          ] = data;
          return [...currentMainStops];
        });

        setInvalidStops(invalidStops => {
          const newInvalidStops = invalidStops.filter(
            stopRow => stopRow != data.row
          );

          if (!validateStop(data)) {
            newInvalidStops.push(data.row);
          }
          return newInvalidStops;
        });

        if (tableRef.current) {
          tableRef.current.api.refreshCells({
            force: true,
            rowNodes: [node]
          });
        }
      }
    }),
    [validateStop]
  );

  let content = (
    <Grid
      container
      direction="column"
      style={{ height: "100%", maxWidth: "100%", overflow: "hidden" }}
    >
      <Grid item>
        <DragAndDrop type="stops" handleFetch={loadFileContent} newDesign />
      </Grid>
      <Grid item className={classes.importFileSectionWrapper}>
        <Typography className={classes.importFileSectionTitle}>
          Columns
        </Typography>
        <Typography variant="body2">
          Columns should be in the correct order and fields marked in * are
          required (in case lat and long are not defined).
        </Typography>
        {fields.map((el, i) => (
          <Chip
            key={i}
            label={`${el.name}${el.required ? "*" : ""}`}
            className={clsx(classes.chip, {
              [classes.chipDark]: el.required
            })}
          />
        ))}
      </Grid>
      <Grid item className={classes.importFileSectionWrapper}>
        <Typography className={classes.importFileSectionTitle}>
          Example
        </Typography>
        <Box className={classes.exampleTableWrapper}>
          <ExampleTable tableData={servicesExample} />
        </Box>
      </Grid>
    </Grid>
  );

  if (stage === stages.MAPPING_COLUMNS) {
    content = (
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <Typography className={classes.mappingDescription}>
            Map the imported file columns. Please note that if you used the
            template, the columns mapping is done automatically and nothing
            should be done here.
          </Typography>
        </Grid>
        <TableContainer className={classes.mappingTableWrapper}>
          <Table size="small">
            <TableHead className={classes.mappingTableHeader}>
              <TableRow>
                <TableCell>Stops data field</TableCell>
                <TableCell>File columns</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columnIds.map(columnId => (
                <TableRow key={columnId}>
                  <TableCell>
                    <Typography className={classes.mappingLabel}>
                      {columnTitles[columnId]}
                      {requiredColumnIds.includes(columnId) ? (
                        <span style={{ color: "red" }}>*</span>
                      ) : (
                        ""
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth variant="outlined">
                      <Select
                        value={columnsMapping[columnId]}
                        onChange={event =>
                          setColumnsMapping(theColumnsMapping => ({
                            ...theColumnsMapping,
                            [columnId]: event.target.value
                          }))
                        }
                        displayEmpty
                        MenuProps={{
                          PopoverClasses: {
                            root: classes.columnSelectPopoverRoot
                          }
                        }}
                      >
                        {fileColumnIds.map(fileColumnId => (
                          <MenuItem key={fileColumnId} value={fileColumnId}>
                            {fileColumnId}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    );
  } else if (stage === stages.INTERACTIVE_TABLE) {
    content = (
      <div
        className="ag-theme-routetitan"
        style={{ height: "calc(100% - 94px)", width: "100%" }}
      >
        <div className={clsx(classes.showOnlyRowsWithProblems)}>
          <span
            className={classes.showOnlyRowsWithProblemsPulse}
            ref={showOnlyRowsWithProblemPulseRef}
          />
          <FormControlLabel
            control={
              <Switch
                value={showOnlyInvalidStops}
                onChange={event => {
                  setShowOnlyInvalidStops(event.target.checked);
                }}
                color="primary"
              />
            }
            label="Show only rows with problems"
          />
        </div>
        <AgGridReact
          ref={tableRef}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          gridOptions={{
            stopEditingWhenCellsLoseFocus: true
          }}
          rowData={
            showOnlyInvalidStops
              ? mainStops.filter(stop => invalidStops.includes(stop.row))
              : mainStops
          }
        />
        <div>
          <Typography className={classes.tableInfo}>
            {mainStops.length} {mainStops.length === 1 ? "row" : "rows"}
            {invalidStops.length > 0 && (
              <b>
                • {invalidStops.length}{" "}
                {invalidStops.length === 1 ? "row" : "rows"} with problem
              </b>
            )}
          </Typography>
        </div>
      </div>
    );
  } else if (stage === stages.VALIDATION_RESULT) {
    content = (
      <div style={{ maxWidth: "100%", overflow: "auto" }}>
        {validationResult.valid.length > 0 && (
          <>
            <Typography>
              Valid stops ({validationResult.valid.length})
            </Typography>
            <DataTable tableData={validationResult.valid} validRows services />
          </>
        )}
        {validationResult.invalid.length > 0 && (
          <>
            <Typography>
              Invalid stops ({validationResult.invalid.length})
            </Typography>
            <DataTable
              tableData={validationResult.invalid.map(
                invalidStop => invalidStop[0]
              )}
              invalidRows
              services
            />
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={uploadStopsOpen}
        classes={{
          root: classes.dialogRoot,
          paper: classes.dialogPaper
        }}
      >
        <DialogTitle disableTypography>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <IconButton
                onClick={() => {
                  setUploadStopsOpen(false);
                  resetState();
                  history.replace(`/${project.id}`);
                }}
              >
                <ClearOutlinedIcon />
              </IconButton>
            </Grid>
            <Grid item xs>
              <TitleWithEducationalMaterials
                title={"Upload Stops"}
                configId="upload_stops"
                sectionName={"upload stops"}
                showArticle
                showVideo
                showDivider
              />
            </Grid>
            {stage === stages.CHOOSE_FILE && (
              <Grid item>
                <Button
                  className={classes.simpleVersionButton}
                  onClick={() => {
                    setUploadStopsOpen(false);
                    resetState();
                    history.replace(`/${project.id}/upload-stops-simple`);
                  }}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>Use simple version</Grid>
                  </Grid>
                </Button>
              </Grid>
            )}
            {(stage === stages.INTERACTIVE_TABLE ||
              stage === stages.VALIDATION_RESULT) && (
              <Grid item>
                <Button
                  color="primary"
                  onClick={() => {
                    setDownloadRowsDialogOpen(true);
                  }}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <SaveAltOutlinedIcon color="primary" fontSize="small" />
                    </Grid>
                    <Grid item>Download rows</Grid>
                  </Grid>
                </Button>
              </Grid>
            )}
          </Grid>
        </DialogTitle>
        <DialogContent dividers>{content}</DialogContent>
        {stage !== stages.CHOOSE_FILE && (
          <DialogActions>
            {stage === stages.MAPPING_COLUMNS && (
              <Button
                onClick={validateMappingAndGoNext}
                variant="contained"
                color="primary"
                className={classes.actionButton}
              >
                Next
              </Button>
            )}
            {stage === stages.INTERACTIVE_TABLE && (
              <Button
                onClick={submitValidateStops}
                variant="contained"
                color="primary"
                className={classes.actionButton}
              >
                Validate
              </Button>
            )}
            {stage === stages.VALIDATION_RESULT && (
              <Button
                onClick={addStops}
                variant="contained"
                color="primary"
                className={classes.actionButton}
              >
                Submit
              </Button>
            )}
          </DialogActions>
        )}
        {loading && (
          <div className={classes.loadingWrapper}>
            <CircularProgress />
          </div>
        )}
      </Dialog>
      <Dialog
        classes={{
          root: classes.otherDialogRoots
        }}
        open={downloadRowsDialogOpen}
        onClose={() => setDownloadOnlyInvalidFields(false)}
      >
        <DialogTitle disableTypography>
          <Typography className={classes.generalDialogTitle}>
            Download rows?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                onChange={event =>
                  setDownloadOnlyInvalidFields(event.target.checked)
                }
                color="primary"
                value={downloadOnlyInvalidFields}
              />
            }
            label="Download only rows with problems"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDownloadOnlyInvalidFields(false);
              setDownloadRowsDialogOpen(false);
            }}
            className={classes.cancelButton}
            variant="text"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.actionButton}
            onClick={() => {
              setDownloadOnlyInvalidFields(false);
              if (stage === stages.INTERACTIVE_TABLE) {
                if (downloadOnlyInvalidFields) {
                  exportToXlsx(
                    "invalid_stops.xlsx",
                    toXlsxExportData(
                      mainStops.filter(stop => invalidStops.includes(stop.row))
                    )
                  );
                } else {
                  exportToXlsx("stops.xlsx", toXlsxExportData(mainStops));
                }
              } else if (stage === stages.VALIDATION_RESULT) {
                downloadValidationResultRows(downloadOnlyInvalidFields);
              }
              setDownloadRowsDialogOpen(false);
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function mapStateToProps(state) {
  return {
    uploadStopsOpen: state.common.uploadStopsOpen,
    project: state.project,
    plan:
      (state.auth.userData.organisationSite &&
        state.auth.userData.organisationSite.organisation.plan) ||
      {}
  };
}

export default connect(mapStateToProps, {
  setUploadStopsOpen,
  setInfoDialog,
  setProject
})(UploadStopsDialog);
